'use client'

import React, { forwardRef, useEffect, useRef, useState } from 'react'
import { cn } from '../lib/utils'
import { colors } from '../tokens/colors'
import { LucideIcon, Calendar, Clock, X } from 'lucide-react'

// Tipos base do date input
type DateInputSize = 'sm' | 'md' | 'lg'
type DateInputVariant = 'default' | 'error' | 'success'

// Interface para aparência do date input
interface DateInputAppearance {
  backgroundColor?: string
  textColor?: string
  borderColor?: string
  placeholderColor?: string
}

interface DateInputProps {
  // Valor e mudança — contrato SEMPRE em formato máquina:
  // 'YYYY-MM-DD' (date) | 'HH:MM' (time) | 'YYYY-MM-DDTHH:MM' (datetime-local)
  value?: string
  onChange?: (value: string) => void
  /** Chamado depois do blur interno (que restaura a máscara). */
  onBlur?: (value: string) => void

  // Configuração visual
  variant?: DateInputVariant
  size?: DateInputSize

  // Estados
  error?: boolean
  success?: boolean
  disabled?: boolean

  // Ícones
  leftIcon?: LucideIcon
  /** Some com o ícone à esquerda — para campos em linha, onde o espaço é curto. */
  showLeftIcon?: boolean

  // Labels e mensagens
  label?: string
  helperText?: string
  errorMessage?: string
  placeholder?: string

  // Customização
  appearance?: DateInputAppearance

  // Layout
  fullWidth?: boolean
  className?: string

  // Comportamento específico do DateInput
  clearable?: boolean
  required?: boolean
  min?: string // Data mínima (YYYY-MM-DD)
  max?: string // Data máxima (YYYY-MM-DD)
  type?: 'date' | 'time' | 'datetime-local'
  step?: number | string

  // Propriedades HTML nativas
  name?: string
  id?: string
}

// Presets de variantes (reutilizando do Input)
const variantPresets: Record<DateInputVariant, DateInputAppearance> = {
  default: {
    backgroundColor: colors.background.white,
    textColor: colors.text.primary,
    borderColor: colors.border.primary,
    placeholderColor: colors.text.tertiary,
  },
  error: {
    backgroundColor: colors.status.error.background,
    textColor: colors.status.error.text,
    borderColor: colors.status.error.text,
    placeholderColor: colors.text.tertiary,
  },
  success: {
    backgroundColor: colors.status.success.background,
    textColor: colors.status.success.text,
    borderColor: colors.status.success.text,
    placeholderColor: colors.text.tertiary,
  },
}

// ---------------------------------------------------------------------------
// Máscara pt-BR: o navegador formata input[type=date] pela língua DELE (o
// atributo lang é ignorado pelo Chrome), então digitação/exibição usam um
// input de texto mascarado dd/mm/aaaa (ou HH:MM), e o seletor nativo continua
// disponível pelo ícone (input escondido + showPicker()).
// ---------------------------------------------------------------------------

const maskDateDigits = (digits: string): string => {
  const d = digits.slice(0, 8)
  if (d.length <= 2) return d
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`
}

const maskTimeDigits = (digits: string): string => {
  const d = digits.slice(0, 4)
  if (d.length <= 2) return d
  return `${d.slice(0, 2)}:${d.slice(2)}`
}

/** 'YYYY-MM-DD' → 'dd/mm/aaaa' (ou '' quando vazio). */
const dateValueToDisplay = (value: string): string =>
  value ? value.split('-').reverse().join('/') : ''

/** 'dd/mm/aaaa' completo e válido → 'YYYY-MM-DD'; senão null. */
const displayToDateValue = (display: string): string | null => {
  const digits = display.replace(/\D/g, '')
  if (digits.length !== 8) return null
  const day = Number(digits.slice(0, 2))
  const month = Number(digits.slice(2, 4))
  const year = Number(digits.slice(4, 8))
  const date = new Date(year, month - 1, day)
  const valid =
    date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
  if (!valid) return null
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/** 'HH:MM' completo e válido → 'HH:MM'; senão null. */
const displayToTimeValue = (display: string): string | null => {
  const digits = display.replace(/\D/g, '')
  if (digits.length !== 4) return null
  const hours = Number(digits.slice(0, 2))
  const minutes = Number(digits.slice(2, 4))
  if (hours > 23 || minutes > 59) return null
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>((
  {
    value,
    onChange,
    onBlur,
    variant = 'default',
    size = 'md',
    error = false,
    success = false,
    disabled = false,
    leftIcon,
    showLeftIcon = true,
    label,
    helperText,
    errorMessage,
    placeholder,
    appearance,
    fullWidth = false,
    className,
    clearable = false,
    required = false,
    min,
    max,
    type: inputType = 'datetime-local',
    step,
    name,
    id,
    ...props
  },
  ref
) => {
  const [isFocused, setIsFocused] = useState(false)
  const pickerRef = useRef<HTMLInputElement>(null)

  const isMasked = inputType === 'date' || inputType === 'time'
  const valueToDisplay = (v: string) =>
    inputType === 'date' ? dateValueToDisplay(v) : v
  const [displayText, setDisplayText] = useState(() => valueToDisplay(value || ''))

  // Sincroniza a exibição quando o valor externo muda (picker, reset, load).
  useEffect(() => {
    if (isMasked) setDisplayText(valueToDisplay(value || ''))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, inputType])

  const LeftIcon: LucideIcon = leftIcon || (inputType === 'time' ? Clock : Calendar)

  // Determinar variante baseada nos estados
  const currentVariant = error ? 'error' : success ? 'success' : variant

  // Determinar aparência final
  const preset = variantPresets[currentVariant]
  const finalAppearance: DateInputAppearance = {
    backgroundColor: appearance?.backgroundColor || (disabled ? colors.palette.gray[50] : preset.backgroundColor),
    textColor: appearance?.textColor || (disabled ? colors.palette.gray[400] : preset.textColor),
    borderColor: appearance?.borderColor || (disabled ? colors.palette.gray[200] : preset.borderColor),
    placeholderColor: appearance?.placeholderColor || preset.placeholderColor,
  }

  // Classes de tamanho
  const sizes = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-3 text-base',
    lg: 'px-5 py-4 text-lg',
  }

  // Handlers
  const handleNativeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value)
  }

  const handleMaskedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '')
    const masked = inputType === 'date' ? maskDateDigits(digits) : maskTimeDigits(digits)
    setDisplayText(masked)

    if (digits.length === 0) {
      onChange?.('')
      return
    }
    const parsed = inputType === 'date' ? displayToDateValue(masked) : displayToTimeValue(masked)
    if (parsed) {
      onChange?.(parsed)
    }
  }

  const handleMaskedBlur = () => {
    setIsFocused(false)
    // Digitação incompleta/ inválida volta para o último valor válido.
    setDisplayText(valueToDisplay(value || ''))
    onBlur?.(value || '')
  }

  const handleClear = () => {
    onChange?.('')
  }

  const openPicker = () => {
    if (disabled) return
    const picker = pickerRef.current
    if (!picker) return
    try {
      picker.showPicker()
    } catch {
      picker.focus()
    }
  }

  const handleFocus = () => {
    setIsFocused(true)
  }

  const handleBlur = () => {
    setIsFocused(false)
    onBlur?.(value || '')
  }

  // Classes base
  const baseClasses = 'border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary'

  // Classes do container
  const containerClasses = cn(
    'relative',
    fullWidth ? 'w-full' : 'w-auto'
  )

  // Classes do input
  const inputClasses = cn(
    baseClasses,
    sizes[size],
    fullWidth && 'w-full',
    showLeftIcon && 'pl-10',
    clearable && value && 'pr-10',
    disabled && 'cursor-not-allowed',
    className
  )

  // Estilos inline
  const inputStyles: React.CSSProperties = {
    backgroundColor: finalAppearance.backgroundColor,
    color: finalAppearance.textColor,
    borderColor: isFocused ? colors.brand.primary : finalAppearance.borderColor,
  }

  // Classes dos ícones
  const iconClasses = 'absolute top-1/2 transform -translate-y-1/2 w-5 h-5'
  const leftIconClasses = cn(iconClasses, 'left-3')
  const rightIconClasses = cn(iconClasses, 'right-3')

  const defaultPlaceholder = inputType === 'date' ? 'dd/mm/aaaa' : inputType === 'time' ? 'hh:mm' : undefined

  return (
    <div className={cn('space-y-2', fullWidth && 'w-full')}>
      {/* Label */}
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium"
          style={{ color: finalAppearance.textColor }}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Input Container */}
      <div className={containerClasses}>
        {/* Left Icon — nos tipos mascarados abre o seletor nativo */}
        {!showLeftIcon ? null : isMasked ? (
          <button
            type="button"
            tabIndex={-1}
            onClick={openPicker}
            disabled={disabled}
            aria-label={inputType === 'date' ? 'Abrir calendário' : 'Abrir seletor de hora'}
            className={cn(leftIconClasses, 'flex items-center justify-center', disabled ? 'cursor-not-allowed' : 'cursor-pointer')}
          >
            <LeftIcon className="w-5 h-5" style={{ color: finalAppearance.placeholderColor }} />
          </button>
        ) : (
          <LeftIcon
            className={leftIconClasses}
            style={{ color: finalAppearance.placeholderColor }}
          />
        )}

        {isMasked ? (
          <>
            {/* Exibição/digitação mascarada em pt-BR */}
            <input
              {...props}
              ref={ref}
              id={id}
              name={name}
              type="text"
              inputMode="numeric"
              // Largura natural do conteúdo (hh:mm / dd/mm/aaaa), com um
              // caractere de folga. Sem isso o input de texto assume ~20
              // caracteres e estoura layouts em linha que antes usavam o input
              // nativo, bem mais estreito.
              size={inputType === 'date' ? 11 : 6}
              value={displayText}
              onChange={handleMaskedChange}
              onFocus={handleFocus}
              onBlur={handleMaskedBlur}
              disabled={disabled}
              required={required}
              className={inputClasses}
              style={inputStyles}
              placeholder={placeholder ?? defaultPlaceholder}
            />
            {/* Seletor nativo escondido (aberto pelo ícone) */}
            <input
              ref={pickerRef}
              type={inputType}
              tabIndex={-1}
              aria-hidden="true"
              value={value || ''}
              onChange={handleNativeChange}
              min={min}
              max={max}
              step={step as any}
              disabled={disabled}
              className="absolute left-3 bottom-0 w-px h-px opacity-0 pointer-events-none"
            />
          </>
        ) : (
          <input
            {...props}
            ref={ref}
            id={id}
            name={name}
            type={inputType}
            value={value || ''}
            onChange={handleNativeChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            required={required}
            min={min}
            max={max}
            step={step as any}
            className={inputClasses}
            style={inputStyles}
            placeholder={placeholder}
          />
        )}

        {/* Clear Button */}
        {clearable && value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className={cn(
              rightIconClasses,
              'hover:bg-gray-100 rounded-sm p-1 transition-colors'
            )}
          >
            <X className="w-4 h-4" style={{ color: finalAppearance.placeholderColor }} />
          </button>
        )}
      </div>

      {/* Helper Text / Error Message */}
      {(helperText || errorMessage) && (
        <div className="text-sm">
          {errorMessage ? (
            <span style={{ color: colors.status.error.text }}>
              {errorMessage}
            </span>
          ) : (
            <span style={{ color: colors.text.secondary }}>
              {helperText}
            </span>
          )}
        </div>
      )}
    </div>
  )
})

DateInput.displayName = 'DateInput'
